import Stepper from '@/components/Stepper';
import ToolBar from '@/components/ToolBar';
import { zodResolver } from '@hookform/resolvers/zod';
import { useContext, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { FormProvider, useForm } from 'react-hook-form';
import { PeClientDetails } from './steppers/PeClientDetails';
import { isPrepaidFeatureEnabled, PeCaseDetails } from './steppers/PeCaseDetails';
import PeWoDetails, { WorkOrderSchema } from './steppers/PeWoDetails';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { genUniqueId } from '@/lib/utils';
import {
  ClientDetailsSchema,
  omitFormOnlyWorkOrderFields,
  PeCaseDetailsSchema,
  PeWoDetailsSchema,
  WorkOrderServeTo,
} from './pe.schema';
import { toast } from '@/components/ui/use-toast';
import { displayError } from '@/lib/error';
import { getPhoneNumberValue } from '@/components/FormFieldPhone';
import AlertBox from '@/components/AlertBox';
import { NavigationBlockerContext } from '@/context/NavigationBlockerContext';
import { ServiceCreateUpdateRequest } from '@/types/service.types';
import { useCreateService } from '@/services/pe';
import { useCreateHIWorkOrder } from '@/services/homeInspectionService';
import { Action, AfterAction, PaymentType } from '@/types/workOrder.types';
import { caseTypesWithWitnessFees } from '@/utils/constant';
import { mapPsFormToHiCreatePayload } from './mapPsFormToHiCreatePayload';
import { canCreateLinkedHomeInspection } from './canCreateLinkedHomeInspection';
import {
  attachLinkedHiWorkOrderIds,
  fingerprintHiCreatePayload,
  type PendingLinkedHiByIndex,
  shouldForceIncludePendingHi,
  shouldReusePendingLinkedHi,
} from './psLinkedHiSave';
import { getPsSaveErrorMessage } from './getPsSaveErrorMessage';
import { PsSaveValidationMessage } from './PsSaveValidationMessage';

type PsCreateFormWorkOrder = ServiceCreateUpdateRequest['workOrders'][number] & {
  includeHomeInspection?: boolean;
  includeHomeInspectionManualOverride?: boolean;
  documentNames?: Record<string, string | undefined> | null;
  sameAsPropertyAddress?: boolean;
};

export const PECreate = () => {
  const schemas = [ClientDetailsSchema, PeCaseDetailsSchema, PeWoDetailsSchema];
  const [searchParam, setSearchParam] = useSearchParams();
  const [submitAlertBoxOpen, setSubmitAlertBoxOpen] = useState(false);
  const [steps, setSteps] = useState([
    { title: 'Client Details', isCompleted: false },
    { title: 'Case Details', isCompleted: false },
    { title: 'W/O Details', isCompleted: false },
  ]);
  const navigate = useNavigate();

  const [activeStep, setActiveStep] = useState(0);
  const [currentSchema, setCurrentSchema] = useState(schemas[activeStep]);
  const [services, setServices] = useState<
    { id: string; name: string; type: undefined | string; data: WorkOrderSchema | undefined }[]
  >([{ id: genUniqueId(), name: 'New Service', type: undefined, data: undefined }]);
  /** Per WO index — survives PS-create failure so retry does not recreate unchanged HIs. */
  const [pendingLinkedHiByIndex, setPendingLinkedHiByIndex] = useState<PendingLinkedHiByIndex>({});

  const form = useForm({
    resolver: zodResolver(currentSchema),
    mode: 'all',
  });

  const {
    getValues,
    reset,
    formState: { isDirty },
  } = form;

  const { setIsDirty } = useContext(NavigationBlockerContext);

  useEffect(() => {
    setIsDirty(isDirty);
  }, [isDirty, setIsDirty]);

  const scrollToTop = () => {
    const toolbarElement = document.getElementById('toolbar');
    if (toolbarElement) {
      toolbarElement.scrollIntoView({ block: 'end', behavior: 'smooth' });
    }
  };

  useEffect(() => {
    setSearchParam({ stepper: steps[0].title }, { replace: true });
    reset({
      type: 'PROCESS_SERVICE',
      case: {
        serveBy: new Date(new Date().setDate(new Date().getDate() + 5)),
        rush: null,
      },
    });
  }, []);

  useEffect(() => {
    const activeStep = searchParam.get('stepper');
    if (activeStep) {
      const index = steps.findIndex((step) => step.title === activeStep);
      setActiveStep(index);
    }
  }, [searchParam]);

  const goToStep = async (step: number) => {
    const isValid = await form.trigger();
    if (isValid) {
      if (steps[step].isCompleted || step <= activeStep) {
        setSearchParam({ stepper: steps[step].title });
        setActiveStep(step);
      }
    }
  };

  const nextStep = async () => {
    const isValid = await form.trigger();
    if (isValid) {
      setSteps((prevSteps) =>
        prevSteps.map((step, index) => (index === activeStep ? { ...step, isCompleted: true } : step))
      );
      setSearchParam({ stepper: steps[activeStep + 1].title });
      setActiveStep((prev) => Math.min(prev + 1, steps.length - 1));
    }
  };

  const prevStep = () => {
    setSearchParam({ stepper: steps[activeStep - 1].title });
    setActiveStep((prev) => Math.max(prev - 1, 0));
  };

  useEffect(() => {
    setCurrentSchema(schemas[activeStep]);
    setTimeout(async () => {
      await form.trigger();
      form.clearErrors();
    }, 50);
    setTimeout(() => {
      scrollToTop();
    }, 100);
  }, [activeStep]);

  const { mutate: createService, status: serviceCreateStatus } = useCreateService();
  const { mutateAsync: createHIWorkOrder, status: hiCreateStatus } = useCreateHIWorkOrder();
  const [isSavingWithHi, setIsSavingWithHi] = useState(false);
  const isSavePending = serviceCreateStatus === 'pending' || hiCreateStatus === 'pending' || isSavingWithHi;

  const buildServicePayload = (
    data: ServiceCreateUpdateRequest & {
      case?: ServiceCreateUpdateRequest['case'] & { attorneyName?: string | null };
      workOrders?: PsCreateFormWorkOrder[];
    },
    linkedHiByIndex: Record<number, string | undefined>
  ): ServiceCreateUpdateRequest => {
    const rest = { ...data };
    const caseWithoutAttorneyName = { ...(rest.case ?? {}) };
    delete (caseWithoutAttorneyName as { attorneyName?: string | null }).attorneyName;

    const mappedWorkOrders =
      data?.workOrders?.map((wo) => {
        const documentNames = (wo as { documentNames?: Record<string, string | undefined> | null }).documentNames ?? {};
        const woRest = omitFormOnlyWorkOrderFields(wo);

        return {
          ...woRest,
          phone: getPhoneNumberValue(wo.phone),
          serverId: wo.serveTo === WorkOrderServeTo.SERVER ? wo.serverId : null,
          serverInstructions: wo.serverInstructions,
          privateInfo: wo.privateInfo
            ? {
                ssn: wo.privateInfo.ssn ?? undefined,
                dlNumber: wo.privateInfo.dlNumber ?? undefined,
                dateOfBirth: wo.privateInfo.dateOfBirth ?? undefined,
                others: wo.privateInfo.others ?? undefined,
              }
            : undefined,
          documents: Object.entries(wo.documents || {})
            .filter(([, value]) => value)
            .map(([id]) => ({
              caseDocTypeId: id,
              editedName: documentNames[id]?.trim() ? documentNames[id].trim() : null,
            })),
          vendorDetails:
            wo.serveTo === WorkOrderServeTo.VENDOR
              ? {
                  ...wo.vendorDetails,
                  amount: Number(wo?.vendorDetails?.amount),
                  otherAction: wo.vendorDetails?.action === Action.OTHER ? wo.vendorDetails.otherAction : null,
                  otherAfterAction: wo.vendorDetails?.afterAction?.includes(AfterAction.OTHER)
                    ? wo.vendorDetails.otherAfterAction
                    : null,
                }
              : null,
          payments: [
            ...(wo.payments?.map((payment) => ({
              ...payment,
              amount: Number(payment.amount ?? 0),
              checkNumber: payment.checkNumber ? Number(payment.checkNumber) : undefined,
              paymentType: PaymentType.OPERATING_CHECK,
            })) ?? []),

            ...(wo.hasWitnessFees && wo.witnessFee && caseTypesWithWitnessFees.includes(data.case.typeId)
              ? [
                  {
                    ...wo.witnessFee,
                    amount: Number(wo.witnessFee.amount ?? 0),
                    paymentType: PaymentType.WITNESS_FEE,
                    checkNumber: Number(wo.witnessFee.checkNumber),
                  },
                ]
              : []),
          ],
          teamId: wo.teamId,
          userId: wo.userId,
          ...(isPrepaidFeatureEnabled && {
            isPrepaid: data.isPrepaid,
          }),
        };
      }) ?? [];

    return {
      ...rest,
      case: {
        ...caseWithoutAttorneyName,
        insurance: caseWithoutAttorneyName.insurance
          ? {
              ...caseWithoutAttorneyName.insurance,
              phone: getPhoneNumberValue(caseWithoutAttorneyName?.insurance?.phone),
            }
          : undefined,
        address1: caseWithoutAttorneyName?.address1 ?? undefined,
        address2: caseWithoutAttorneyName?.address2 ?? undefined,
        city: caseWithoutAttorneyName?.city ?? undefined,
        state: caseWithoutAttorneyName?.state ?? undefined,
        zipCode: caseWithoutAttorneyName?.zipCode ?? undefined,
      },
      workOrders: attachLinkedHiWorkOrderIds(mappedWorkOrders, linkedHiByIndex),
    } as ServiceCreateUpdateRequest;
  };

  const saveHandler = async () => {
    const formValues = getValues();
    const data = formValues as ServiceCreateUpdateRequest & { workOrders?: PsCreateFormWorkOrder[] };
    const workOrders = data.workOrders ?? [];

    const onPsSuccess = () => {
      setPendingLinkedHiByIndex({});
      toast({
        variant: 'default',
        description: 'Process service is created successfully.',
        duration: 5000,
      });
      navigate('/process');
    };

    const indexesToLink = workOrders
      .map((wo, index) => {
        const pending = pendingLinkedHiByIndex[index];
        const include =
          Boolean(wo.includeHomeInspection) || shouldForceIncludePendingHi(Boolean(wo.includeHomeInspection), pending);
        return include ? index : -1;
      })
      .filter((index) => index >= 0);

    if (indexesToLink.length === 0) {
      setPendingLinkedHiByIndex({});
      createService(
        { data: buildServicePayload(data, {}) },
        {
          onSuccess: onPsSuccess,
          onError: (error) => {
            displayError(error, 5000);
          },
        }
      );
      return;
    }

    if (!canCreateLinkedHomeInspection()) {
      toast({
        variant: 'destructive',
        description: 'Include Home Inspection is not available for your account or feature flags.',
        duration: 5000,
      });
      return;
    }

    const forcedIndexes = indexesToLink.filter(
      (index) =>
        !workOrders[index]?.includeHomeInspection &&
        shouldForceIncludePendingHi(false, pendingLinkedHiByIndex[index])
    );
    if (forcedIndexes.length > 0) {
      toast({
        variant: 'default',
        description:
          'One or more Home Inspections were already created for this save. They will still be linked to their work orders.',
        duration: 6000,
      });
    }

    setIsSavingWithHi(true);
    try {
      const nextPending: PendingLinkedHiByIndex = { ...pendingLinkedHiByIndex };
      const linkedHiByIndex: Record<number, string | undefined> = {};

      for (const index of indexesToLink) {
        const hiPayload = mapPsFormToHiCreatePayload(formValues, index);
        const nextFingerprint = fingerprintHiCreatePayload(hiPayload);
        const pending = nextPending[index];

        if (shouldReusePendingLinkedHi(pending, nextFingerprint)) {
          linkedHiByIndex[index] = pending.id;
          continue;
        }

        if (pending) {
          toast({
            variant: 'default',
            description: `Form data changed for work order ${index + 1} since Home Inspection ${pending.id} was created. Creating a new Home Inspection.`,
            duration: 6000,
          });
        }

        const hiWorkOrder = await createHIWorkOrder(hiPayload);
        linkedHiByIndex[index] = hiWorkOrder.workOrderId;
        nextPending[index] = { id: hiWorkOrder.workOrderId, fingerprint: nextFingerprint };
      }

      // Drop pending entries for WOs that are no longer included
      Object.keys(nextPending).forEach((key) => {
        const index = Number(key);
        if (!indexesToLink.includes(index)) {
          delete nextPending[index];
        }
      });
      setPendingLinkedHiByIndex(nextPending);

      createService(
        { data: buildServicePayload(data, linkedHiByIndex) },
        {
          onSuccess: () => {
            setIsSavingWithHi(false);
            onPsSuccess();
          },
          onError: (error) => {
            setIsSavingWithHi(false);
            const hiSummary = Object.values(linkedHiByIndex).filter(Boolean).join(', ');
            toast({
              variant: 'destructive',
              description: `Home Inspection(s) ${hiSummary} were created, but Process Service failed: ${getPsSaveErrorMessage(error, 'Process Service create failed')}. Retrying Save will reuse unchanged Home Inspections.`,
              duration: 8000,
            });
          },
        }
      );
    } catch (error) {
      setIsSavingWithHi(false);
      displayError(error, 5000);
    }
  };

  return (
    <div className="w-full scrollbar-hide">
      <div className="w-full">
        <ToolBar title="Process Service Entry" />
        <div className="mt-3 flex justify-center pl-24">
          <Stepper steps={steps} activeStep={activeStep} goToStep={goToStep} />
        </div>

        <FormProvider {...form}>
          {activeStep === 0 && <PeClientDetails />}
          {activeStep === 1 && <PeCaseDetails />}
          {activeStep === 2 && (
            <PeWoDetails services={services} setServices={setServices} showIncludeHomeInspection />
          )}
        </FormProvider>

        <div className="flex flex-col items-end gap-2 mt-4 mb-8 w-full">
          <div className={`flex justify-end w-full gap-2`}>
            {activeStep > 0 && (
              <Button
                variant="outline"
                className="text-primary py-2 px-4 rounded-md border-primary  flex justify-center w-[125px] h-[48px] hover:bg-[#EDF5FF] "
                onClick={prevStep}
              >
                Back
              </Button>
            )}
            {activeStep !== steps.length - 1 && (
              <Button
                className="text-[#fff] py-2 px-4 rounded-md bg-primary  w-[125px] h-[48px] border-primary flex justify-center  hover:bg-[#12375C] hover:text-white disabled:bg-primary "
                onClick={nextStep}
                disabled={activeStep === steps.length - 1 || !form.formState.isValid}
              >
                Next
              </Button>
            )}

            {activeStep === steps.length - 1 && (
              <AlertBox
                open={submitAlertBoxOpen}
                triggerButton={
                  <Button
                    variant="outline"
                    className="text-[#fff] py-2 px-4 rounded-md bg-primary w-[125px] h-[48px] border-primary flex justify-center hover:bg-[#12375C] hover:text-white "
                    onClick={() => {
                      setSubmitAlertBoxOpen(true);
                    }}
                    disabled={!form.formState.isValid || isSavePending}
                  >
                    Save
                  </Button>
                }
                title="Do you want to create a new entry?"
                description="This action will create a new entry in the database."
                cancelBtnLabel="Cancel"
                confirmBtnLabel="Save"
                cancelBtnClick={() => {
                  setSubmitAlertBoxOpen(false);
                }}
                isLoading={isSavePending}
                confirmBtnClick={saveHandler}
              />
            )}
          </div>
          {activeStep === steps.length - 1 && !form.formState.isValid && (
            <FormProvider {...form}>
              <PsSaveValidationMessage schema={PeWoDetailsSchema} />
            </FormProvider>
          )}
        </div>
      </div>
    </div>
  );
};
